import i18n from "@shared/i18n/i18n";

export interface SocialMediaPlatform {
  labelKey: string;
  urlRegEx: RegExp;
}

export const SOCIAL_MEDIA_PLATFORMS = {
  FACEBOOK: "facebook",
  INSTAGRAM: "instagram",
  LINKEDIN: "linkedin",
  YOUTUBE: "youtube",
  X: "x",
  TIKTOK: "tiktok",
  PERSONAL_WEBSITE: "website",
} as const;

export const SOCIAL_MEDIA_VALIDATION: Record<string, SocialMediaPlatform> = {
  [SOCIAL_MEDIA_PLATFORMS.FACEBOOK]: {
    labelKey: "socialMedia.platforms.facebook",
    urlRegEx: /^https:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9.-]+$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.INSTAGRAM]: {
    labelKey: "socialMedia.platforms.instagram",
    urlRegEx: /^https:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.LINKEDIN]: {
    labelKey: "socialMedia.platforms.linkedin",
    urlRegEx: /^https:\/\/(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9_-]+$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.YOUTUBE]: {
    labelKey: "socialMedia.platforms.youtube",
    urlRegEx:
      /^https:\/\/(www\.)?youtube\.com\/(c|channel|user)\/[a-zA-Z0-9_-]+$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.X]: {
    labelKey: "socialMedia.platforms.x",
    urlRegEx: /^https:\/\/(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9_]+$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.TIKTOK]: {
    labelKey: "socialMedia.platforms.tiktok",
    urlRegEx: /^https:\/\/(www\.)?tiktok\.com\/@?[a-zA-Z0-9._]+$/,
  },
  [SOCIAL_MEDIA_PLATFORMS.PERSONAL_WEBSITE]: {
    labelKey: "socialMedia.platforms.website",
    urlRegEx: /^https?:\/\/[a-zA-Z0-9\-._~:/?#@!$&'()*+,;=%]+$/,
  },
};

/**
 * Validates a social media URL based on the platform
 * @param platform - The social media platform key
 * @param url - The URL to validate
 * @returns Object containing validation result and error message
 */
export function validateSocialMediaUrl(
  platform: string,
  url: string
): {
  isValid: boolean;
  error?: string;
} {
  if (!url.trim()) {
    return {
      isValid: false,
      error: i18n.t("socialMedia.validation.required"),
    };
  }

  const validation = SOCIAL_MEDIA_VALIDATION[platform];
  if (!validation) {
    return {
      isValid: false,
      error: i18n.t("socialMedia.validation.unknownPlatform"),
    };
  }

  const isValid = validation.urlRegEx.test(url.trim());
  if (!isValid) {
    const platformLabel = i18n.t(validation.labelKey);
    return {
      isValid: false,
      error: i18n.t("socialMedia.validation.invalidUrl", {
        platform: platformLabel,
      }),
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Get validation info for a specific platform
 * @param platform - The social media platform key
 * @returns Platform validation info or undefined
 */
export function getSocialMediaValidation(
  platform: string
): SocialMediaPlatform | undefined {
  return SOCIAL_MEDIA_VALIDATION[platform];
}

/**
 * Get all available social media platforms
 * @returns Array of platform keys and labels
 */
export function getAllSocialMediaPlatforms(): Array<{
  value: string;
  label: string;
}> {
  return Object.entries(SOCIAL_MEDIA_VALIDATION).map(([key, value]) => ({
    value: key,
    label: i18n.t(value.labelKey),
  }));
}
